/* ================================================================
   NUTKASH THEME v6 — nutkash.js
   Vanilla JS · Event-delegated · No jQuery · Modular
   ================================================================ */

'use strict';

/* ── CART STATE ── */
const NutkashCart = (() => {
  let _count = 0;

  function updateBadge(count) {
    _count = count;
    document.querySelectorAll('.cart-badge').forEach(el => {
      el.textContent = count;
      el.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
      el.classList.toggle('has-items', count > 0);
    });
  }

  async function getCart() {
    try {
      const res = await fetch('/cart.js');
      const data = await res.json();
      updateBadge(data.item_count);
      return data;
    } catch (e) { return null; }
  }

  async function addItem(variantId, qty = 1, properties = {}) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: qty, properties })
      });
      if (!res.ok) throw new Error('Add failed');
      await refreshDrawer();
      return true;
    } catch (e) {
      NutkashToast.show('Unable to add item. Please try again.', 'error');
      return false;
    }
  }

  async function changeItem(key, qty) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      await refreshDrawer();
    } catch (e) {}
  }

  async function removeItem(key) {
    await changeItem(key, 0);
  }

  async function refreshDrawer() {
    const cart = await getCart();
    if (cart) NutkashDrawer.render(cart);
  }

  return { getCart, addItem, changeItem, removeItem, updateBadge };
})();

/* ── TOAST ── */
const NutkashToast = (() => {
  let timer = null;
  function show(msg, type = 'success') {
    const el = document.getElementById('toast');
    if (!el) return;
    el.textContent = msg;
    el.className = 'toast show';
    if (type === 'error') el.style.background = 'var(--berry)';
    else el.style.background = 'var(--ink)';
    clearTimeout(timer);
    timer = setTimeout(() => { el.classList.remove('show'); }, 2800);
  }
  return { show };
})();

/* ── CART DRAWER ── */
const NutkashDrawer = (() => {
  function open() {
    const drawer = document.getElementById('cartDrawer');
    const overlay = document.getElementById('cartOverlay');
    if (!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay && overlay.classList.add('on');
    document.body.style.overflow = 'hidden';
    drawer.querySelector('.cart-close')?.focus();
  }

  function close() {
    const drawer = document.getElementById('cartDrawer');
    const overlay = document.getElementById('cartOverlay');
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay && overlay.classList.remove('on');
    document.body.style.overflow = '';
    document.querySelector('.header-icon[data-cart]')?.focus();
  }

  function render(cart) {
    const itemsEl = document.getElementById('cartItems');
    const subtotalEl = document.getElementById('cartSubtotal');
    const countEl = document.getElementById('cartCount');

    NutkashCart.updateBadge(cart.item_count);
    if (countEl) countEl.textContent = `(${cart.item_count})`;

    if (!itemsEl) return;

    if (cart.items.length === 0) {
      itemsEl.innerHTML = `
        <div class="cart-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 16px;display:block;color:var(--ink-3)"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          <p style="color:var(--ink-3);font-size:var(--text-sm)">${window.NutkashStrings?.cartEmpty || 'Your cart is empty.'}</p>
          <a href="/collections/all" class="btn" style="margin-top:var(--sp-5)">Shop now</a>
        </div>`;
      if (subtotalEl) subtotalEl.textContent = '₹0';
      return;
    }

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item-img">
          <img src="${item.featured_image?.url ? item.featured_image.url + '&width=160' : ''}"
               alt="${item.product_title}" loading="lazy" width="80" height="80">
        </div>
        <div>
          <div class="cart-item-name">${item.product_title}</div>
          <div class="cart-item-variant">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
          <div class="cart-qty">
            <button class="cart-qty-btn" data-action="decrement" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
            <span class="cart-qty-val">${item.quantity}</span>
            <button class="cart-qty-btn" data-action="increment" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <div style="text-align:right;display:flex;flex-direction:column;justify-content:space-between">
          <div class="cart-item-price">${formatMoney(item.line_price)}</div>
          <button class="cart-item-remove" data-action="remove" data-key="${item.key}" aria-label="Remove ${item.product_title}">Remove</button>
        </div>
      </div>`).join('');

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    const note = document.getElementById('cartShippingNote');
    if (note) {
      const threshold = window.NutkashStrings?.freeShippingThreshold || 2999;
      const remaining = threshold * 100 - cart.total_price;
      note.textContent = remaining > 0
        ? `Add ${formatMoney(remaining)} more for free shipping`
        : 'You qualify for free shipping!';
    }
  }

  function formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }

  return { open, close, render };
})();

/* ── MOBILE MENU ── */
const NutkashMenu = (() => {
  let focusableEls = [];

  function open() {
    const nav = document.getElementById('mobileNav');
    const btn = document.getElementById('menuBtn');
    const overlay = document.getElementById('menuOverlay');
    if (!nav) return;
    nav.classList.add('open');
    nav.removeAttribute('aria-hidden');
    overlay?.classList.add('on');
    btn?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    focusableEls = [...nav.querySelectorAll('a,button,[tabindex]:not([tabindex="-1"])')];
    focusableEls[0]?.focus();
  }

  function close() {
    const nav = document.getElementById('mobileNav');
    const btn = document.getElementById('menuBtn');
    const overlay = document.getElementById('menuOverlay');
    if (!nav) return;
    nav.classList.remove('open');
    nav.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('on');
    btn?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    btn?.focus();
  }

  function trapFocus(e) {
    if (!document.getElementById('mobileNav')?.classList.contains('open')) return;
    if (!focusableEls.length) return;
    const first = focusableEls[0];
    const last = focusableEls[focusableEls.length - 1];
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    if (e.key === 'Escape') close();
  }

  return { open, close, trapFocus };
})();

/* ── ATC BUTTON MORPHING ── */
function morphAtcBtn(btn, state) {
  const labels = {
    idle:    btn.dataset.labelIdle    || window.NutkashStrings?.addToCart || 'Add to Cart',
    adding:  btn.dataset.labelAdding  || window.NutkashStrings?.adding    || 'Adding…',
    added:   btn.dataset.labelAdded   || window.NutkashStrings?.added     || 'Added ✓'
  };
  btn.dataset.state = state;
  btn.classList.remove('adding', 'added');
  if (state === 'adding') { btn.classList.add('adding'); btn.textContent = labels.adding; btn.disabled = true; }
  else if (state === 'added') { btn.classList.add('added'); btn.textContent = labels.added; btn.disabled = true; }
  else { btn.textContent = labels.idle; btn.disabled = false; }
}

/* ── PRODUCT FORM ── */
function initProductForms() {
  document.querySelectorAll('[data-product-form]').forEach(form => {
    const atcBtn = form.querySelector('.atc-btn, .product-card-atc');
    if (!atcBtn) return;

    form.addEventListener('submit', async e => {
      e.preventDefault();
      const variantId = form.querySelector('[name="id"]')?.value;
      const qty = parseInt(form.querySelector('[name="quantity"]')?.value || 1);
      if (!variantId) return;

      morphAtcBtn(atcBtn, 'adding');
      const ok = await NutkashCart.addItem(variantId, qty);
      if (ok) {
        morphAtcBtn(atcBtn, 'added');
        NutkashDrawer.open();
        NutkashToast.show(window.NutkashStrings?.added || 'Added to cart ✓');
        setTimeout(() => morphAtcBtn(atcBtn, 'idle'), 2000);
      } else {
        morphAtcBtn(atcBtn, 'idle');
      }
    });
  });
}

/* ── VARIANT SELECTOR ── */
function initVariantSelectors() {
  document.querySelectorAll('.variant-options').forEach(container => {
    container.addEventListener('click', e => {
      const btn = e.target.closest('.variant-btn');
      if (!btn || btn.classList.contains('sold-out')) return;
      container.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const form = btn.closest('[data-product-form]') || btn.closest('form');
      const option = btn.closest('.variant-selector')?.dataset.option;
      updateVariant(form, option, btn.dataset.value);
    });
  });
}

function updateVariant(form, changedOption, changedValue) {
  if (!form || !window.__productData) return;
  const selections = {};
  form.querySelectorAll('.variant-selector').forEach(sel => {
    selections[sel.dataset.option] = sel.querySelector('.variant-btn.active')?.dataset.value;
  });
  if (changedOption) selections[changedOption] = changedValue;

  /* Update the selected label text for the changed option */
  if (changedOption) {
    const idx = changedOption.replace('option', '');
    const labelEl = form.querySelector(`#optionLabel${idx}`);
    if (labelEl) labelEl.textContent = changedValue;
  }

  /* Re-evaluate sold-out state for every option button given current selections */
  const variants = window.__productData.variants;
  form.querySelectorAll('.variant-selector').forEach(sel => {
    const optKey = sel.dataset.option;
    const optIdx = parseInt(optKey.replace('option', '')) - 1;
    sel.querySelectorAll('.variant-btn').forEach(btn => {
      const val = btn.dataset.value;
      const available = variants.some(v => {
        if (!v.available) return false;
        if (v.options[optIdx] !== val) return false;
        return v.options.every((opt, i) => {
          const key = `option${i + 1}`;
          if (key === optKey) return true;
          return !selections[key] || opt === selections[key];
        });
      });
      btn.classList.toggle('sold-out', !available);
      btn.disabled = !available;
      if (!available) btn.setAttribute('aria-label', `${val} — sold out`);
      else btn.removeAttribute('aria-label');
    });
  });

  const variant = findVariant(variants, selections);
  if (!variant) return;

  const idInput = form.querySelector('[name="id"]');
  if (idInput) idInput.value = variant.id;

  const priceEl = form.closest('.product-info')?.querySelector('.product-price');
  if (priceEl) priceEl.textContent = '₹' + (variant.price / 100).toLocaleString('en-IN');

  const atcBtn = form.querySelector('.atc-btn');
  if (atcBtn) {
    if (!variant.available) {
      atcBtn.disabled = true;
      atcBtn.textContent = window.NutkashStrings?.soldOut || 'Sold Out';
    } else {
      morphAtcBtn(atcBtn, 'idle');
    }
  }

  const stickyAtc = document.getElementById('stickyAtcBtn');
  if (stickyAtc) {
    stickyAtc.dataset.variantId = variant.id;
    stickyAtc.disabled = !variant.available;
  }
}

function findVariant(variants, selections) {
  return variants?.find(v =>
    v.options.every((opt, i) => opt === selections[`option${i + 1}`])
  );
}

/* ── QUANTITY SELECTOR ── */
function initQtySelectors() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('.qty-btn');
    if (!btn) return;
    const wrapper = btn.closest('.qty-selector');
    const valEl = wrapper?.querySelector('.qty-val');
    const input = wrapper?.querySelector('input[name="quantity"]');
    if (!valEl) return;
    let val = parseInt(valEl.textContent) || 1;
    if (btn.dataset.dir === 'up')   val = Math.min(val + 1, 10);
    if (btn.dataset.dir === 'down') val = Math.max(val - 1, 1);
    valEl.textContent = val;
    if (input) input.value = val;
  });
}

/* ── IMAGE GALLERY ── */
function initGallery() {
  document.querySelectorAll('.product-thumbs').forEach(thumbs => {
    thumbs.addEventListener('click', e => {
      const thumb = e.target.closest('.product-thumb');
      if (!thumb) return;
      thumbs.querySelectorAll('.product-thumb').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      const mainImg = thumb.closest('.product-gallery')?.querySelector('#productMainImg');
      if (mainImg && thumb.dataset.src) {
        mainImg.srcset = '';
        mainImg.src = thumb.dataset.src;
      }
    });
    thumbs.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') e.target.click();
    });
  });
}

/* ── STICKY ATC (IntersectionObserver) ── */
function initStickyAtc() {
  const buyBox = document.getElementById('productBuyBox');
  const stickyBar = document.getElementById('stickyAtcBar');
  if (!buyBox || !stickyBar) return;

  const observer = new IntersectionObserver(([entry]) => {
    stickyBar.classList.toggle('visible', !entry.isIntersecting);
  }, { threshold: 0.1 });
  observer.observe(buyBox);

  document.getElementById('stickyAtcBtn')?.addEventListener('click', async function() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;
    morphAtcBtn(this, 'adding');
    const ok = await NutkashCart.addItem(variantId, 1);
    if (ok) {
      morphAtcBtn(this, 'added');
      NutkashDrawer.open();
      setTimeout(() => morphAtcBtn(this, 'idle'), 2000);
    } else morphAtcBtn(this, 'idle');
  });
}

/* ── FAQ ACCORDION ── */
function initFaq() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('.faq-q');
    if (!btn) return;
    const item = btn.closest('.faq-item');
    if (!item) return;
    const isOpen = item.classList.contains('open');
    item.closest('.faq-list')?.querySelectorAll('.faq-item.open').forEach(o => {
      o.classList.remove('open');
      o.querySelector('.faq-q')?.setAttribute('aria-expanded', 'false');
    });
    if (!isOpen) {
      item.classList.add('open');
      btn.setAttribute('aria-expanded', 'true');
    }
  });
}

/* ── SCROLL REVEAL ── */
const NK_REVEAL_AUTO = [
  '.nk-section-title',
  '.nk-stat-card',
  '.nkht-step',
  '.nk-timeline-item',
  '.nk-for-item',
  '.nk-comp-card',
  '.nk-sourcing-block',
  '.hp-cards .hp-card',
  '.nk-bundle-card',
  '.nk-wol-card',
  '.nk-prod-feature'
].join(',');

function revealAll() {
  document.querySelectorAll('.reveal').forEach(el => el.classList.add('in'));
}
function initReveal() {
  if (window.Shopify && window.Shopify.designMode) { revealAll(); return; }
  if (!('IntersectionObserver' in window)) { revealAll(); return; }
  const obs = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in');
        obs.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
  document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
  /* Auto-apply reveal to Nutkash UI elements, staggered within each parent */
  const seen = new WeakMap();
  document.querySelectorAll(NK_REVEAL_AUTO).forEach(el => {
    if (el.classList.contains('reveal')) return;
    const parent = el.parentElement;
    const idx = seen.has(parent) ? seen.get(parent) + 1 : 0;
    seen.set(parent, idx);
    el.classList.add('reveal');
    el.style.transitionDelay = Math.min(idx * 0.08, 0.32) + 's';
    obs.observe(el);
  });
}

/* ── SHOPIFY EDITOR EVENTS ── */
document.addEventListener('shopify:section:load',   revealAll);
document.addEventListener('shopify:section:reorder', revealAll);
document.addEventListener('shopify:block:select',   revealAll);

/* ── BACK TO TOP ── */
function initBackToTop() {
  const btn = document.getElementById('backToTop');
  if (!btn) return;
  const onScroll = () => btn.classList.toggle('visible', window.scrollY > 500);
  window.addEventListener('scroll', onScroll, { passive: true });
  btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
}

/* ── CART DRAWER EVENTS ── */
function initCartDrawer() {
  document.addEventListener('click', async e => {
    if (e.target.closest('[data-cart-open]')) {
      NutkashDrawer.open();
      NutkashCart.getCart().then(cart => { if (cart) NutkashDrawer.render(cart); });
      return;
    }
    if (e.target.closest('.cart-close'))       { NutkashDrawer.close(); return; }
    if (e.target.closest('#cartOverlay'))      { NutkashDrawer.close(); return; }

    const qtyBtn = e.target.closest('.cart-qty-btn');
    if (qtyBtn) {
      const key = qtyBtn.dataset.key;
      const qty = parseInt(qtyBtn.dataset.qty);
      if (qtyBtn.dataset.action === 'increment') await NutkashCart.changeItem(key, qty + 1);
      if (qtyBtn.dataset.action === 'decrement') await NutkashCart.changeItem(key, Math.max(0, qty - 1));
      return;
    }

    const removeBtn = e.target.closest('[data-action="remove"]');
    if (removeBtn) { await NutkashCart.removeItem(removeBtn.dataset.key); return; }
  });

  /* Swipe to close on mobile */
  const drawer = document.getElementById('cartDrawer');
  if (!drawer) return;
  let startX = 0;
  drawer.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
  drawer.addEventListener('touchend', e => {
    if (e.changedTouches[0].clientX - startX > 60) NutkashDrawer.close();
  }, { passive: true });
}

/* ── MOBILE MENU EVENTS ── */
function initMobileMenu() {
  document.getElementById('menuBtn')?.addEventListener('click', NutkashMenu.open);
  document.getElementById('menuClose')?.addEventListener('click', NutkashMenu.close);
  document.getElementById('menuOverlay')?.addEventListener('click', NutkashMenu.close);
  document.addEventListener('keydown', NutkashMenu.trapFocus);
}

/* ── MARQUEE (announcement bar) ── */
function initMarquee() {
  const track = document.querySelector('.marq-track');
  if (!track) return;
  const clone = track.innerHTML;
  track.innerHTML = clone + clone;
}

/* ── HERO SLIDESHOW ── */
function initHeroSlideshow() {
  const slides = document.querySelectorAll('.hero-slide');
  const dots   = document.querySelectorAll('.hero-dot');
  if (slides.length < 2) return;
  let current = 0, timer;

  function goTo(idx) {
    slides[current].classList.remove('active');
    dots[current]?.classList.remove('active');
    current = (idx + slides.length) % slides.length;
    slides[current].classList.add('active');
    dots[current]?.classList.add('active');
  }

  function start() { timer = setInterval(() => goTo(current + 1), 5000); }
  function stop()  { clearInterval(timer); }

  dots.forEach((dot, i) => dot.addEventListener('click', () => { stop(); goTo(i); start(); }));
  goTo(0);
  start();
  const hero = document.querySelector('.hero');
  hero?.addEventListener('mouseenter', stop);
  hero?.addEventListener('mouseleave', start);
}

/* ── NEWSLETTER FORM ── */
function initNewsletter() {
  document.querySelectorAll('.newsletter-form').forEach(form => {
    form.addEventListener('submit', e => {
      e.preventDefault();
      const email = form.querySelector('input[type="email"]')?.value;
      if (!email) return;
      const success = form.nextElementSibling;
      form.style.display = 'none';
      if (success) success.style.display = 'block';
      NutkashToast.show('Subscribed! Check your inbox.');
    });
  });
}

/* ── FILTERS (collection) ── */
function initFilters() {
  document.querySelectorAll('.filter-chip').forEach(chip => {
    chip.addEventListener('click', function() {
      this.closest('.collection-filter-bar')
          ?.querySelectorAll('.filter-chip')
          .forEach(c => c.classList.remove('active'));
      this.classList.add('active');
    });
  });
}

/* ── HEADER SCROLL SHADOW ── */
function initHeaderScroll() {
  const header = document.querySelector('.header');
  if (!header) return;
  window.addEventListener('scroll', () => {
    header.style.boxShadow = window.scrollY > 10 ? 'var(--shadow-md)' : '';
  }, { passive: true });
}

/* ── INIT ── */
document.addEventListener('DOMContentLoaded', () => {
  NutkashCart.getCart();
  initMarquee();
  initCartDrawer();
  initMobileMenu();
  initProductForms();
  initVariantSelectors();
  initQtySelectors();
  initGallery();
  initStickyAtc();
  initFaq();
  initReveal();
  initBackToTop();
  initHeroSlideshow();
  initNewsletter();
  initFilters();
  initHeaderScroll();
});
